import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Zap, 
  Users, 
  TrendingUp, 
  Sparkles, 
  CheckCircle, 
  Star,
  ArrowRight,
  Bot,
  Brain,
  Target,
  Rocket,
  Shield,
  Globe,
  Mail,
  MessageSquare,
  Phone,
  BarChart3,
  Calendar,
  Workflow,
  DollarSign
} from 'lucide-react'

export default function LandingPage() {
  const [billingCycle, setBillingCycle] = useState('monthly')

  // Function to scroll to pricing section
  const scrollToPricing = () => {
    const pricingElement = document.getElementById('pricing')
    if (pricingElement) {
      pricingElement.scrollIntoView({ behavior: 'smooth' })
    }
  }

  // Direct payment handlers - live Stripe payment links
  const handlePlanPurchase = (planId) => {
    switch (planId) {
      case 'pro_monthly':
        window.location.href = 'https://buy.stripe.com/8x228raLveY4dOsdGdaVa0B'
        break
      case 'pro_yearly':
        window.location.href = 'https://buy.stripe.com/00w3cv3j36ry11G45DaVa0C'
        break
      case 'agency_yearly':
        window.location.href = 'https://buy.stripe.com/cNi7sLcTD2bieSweKhaVa0D'
        break
      default:
        scrollToPricing()
    }
  }

  const features = [
    {
      icon: Bot,
      title: 'AI-Powered Lead Generation & Outreach',
      description: 'Find and enrich leads, then launch personalized email campaigns using your choice of AI (OpenAI, Claude, Gemini, or Perplexity). Multi-provider email sending with smart fallback keeps your outreach running without interruption.'
    },
    {
      icon: Workflow,
      title: 'Visual Workflow Builder & CRM',
      description: 'Build automations with natural language prompts or drag-and-drop logic using our React Flow canvas. Track every contact, deal stage, and pipeline value in one clean Kanban board.'
    },
    {
      icon: Users,
      title: 'White-Label Agency Platform',
      description: 'Launch your own branded SaaS product with a custom logo, custom domain, multi-tenant client management, partner dashboard, and built-in revenue tracking. Everything you need to resell and scale.'
    },
    {
      icon: Shield,
      title: 'Enterprise-Grade Multi-Tenant Security',
      description: 'Complete data isolation keeps every client\'s information private and protected. Built for agencies managing multiple accounts with zero data crossover.'
    }
  ]

  const plans = [
    {
      id: 'pro_monthly',
      name: 'Professional',
      description: 'Built for operators and teams that want consistent AI-powered outbound and a cleaner sales process.',
      features: [
        'AI-Powered Lead Generation + Personalized Cold Outreach',
        'Visual Workflow Builder',
        'CRM & Pipeline',
        'Multi-Provider Fallback (Gmail/Outlook/Brevo)'
      ],
      buttonText: 'Get Started Now',
      priceText: '$47',
      subtitle: '/month',
      note: 'Monthly access',
      cycle: 'monthly'
    },
    {
      id: 'pro_yearly',
      name: 'Pro Yearly',
      description: 'Best for long-term growth with stronger savings, faster support, and deeper reporting.',
      features: [
        'Everything in Professional',
        '2 Months Free',
        'Priority Email Support',
        'Advanced Analytics'
      ],
      buttonText: 'Save $264 Today',
      priceText: '$300',
      subtitle: '/year',
      note: '',
      cycle: 'yearly',
      badge: '⭐ MOST POPULAR Best Value\n$300/year\n($25/mo) Save $264 + Priority Support'
    },
    {
      id: 'agency_yearly',
      name: 'Agency / White Label',
      description: 'Your founder-special tier for launching a branded SaaS offer with client management built in.',
      features: [
        'Everything in Pro',
        'Full White-Label Rights',
        'Custom Logo & Branding',
        'Custom Domain Support',
        'Multi-Tenant Client Management'
      ],
      buttonText: 'Launch Your Own SaaS',
      priceText: '$497',
      subtitle: '/year',
      note: 'Sell Market Genie under your own logo and domain to your clients',
      cycle: 'yearly',
      featured: true
    }
  ]

  const testimonials = [
    {
      image: "/wall of love/testimonial_1_Arpiet_Malpani.png",
      alt: "Arpiet Malpani testimonial"
    },
    {
      image: "/wall of love/testimonial_2_Dashlo.png",
      alt: "Dashlo testimonial"
    },
    {
      image: "/wall of love/testimonial_3_David_Neira.png",
      alt: "David Neira testimonial"
    },
    {
      image: "/wall of love/Untitled.jpeg4.jpeg",
      alt: "Customer testimonial"
    },
    {
      image: "/wall of love/test.jpeg",
      alt: "Customer testimonial"
    },
    {
      image: "/wall of love/5.jpeg",
      alt: "Customer testimonial"
    },
    {
      image: "/wall of love/testimonial_5_Rodnie_Geiser.png",
      alt: "Rodnie Geiser testimonial"
    },
    {
      image: "/wall of love/testimonial_6_Phillip_Masterson_.png",
      alt: "Phillip Masterson testimonial"
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-500 via-emerald-600 to-slate-500">
      {/* Navigation */}
      <nav className="relative z-50 bg-black/35 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 border border-white/40 rounded-lg flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-white">Market Genie</span>
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-200 hover:text-white transition-colors">Features</a>
              <a href="#testimonials" className="text-gray-200 hover:text-white transition-colors">Reviews</a>
              <Link to="/login" className="text-gray-200 hover:text-white transition-colors">Sign In</Link>
              <a 
                href="https://marketgenie.promotekit.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 transform hover:scale-105 font-semibold shadow-lg"
              >
                Become an Affiliate
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-20 pb-32">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-yellow-500/20 blur-3xl"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-sm md:text-base font-semibold text-yellow-300 uppercase tracking-wide mb-4"
            >
              🚀 MARKET GENIE 2.0 IS NOW LIVE
            </motion.p>

            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-7xl font-bold mb-6"
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
                Launch Your Own Branded Software Agency This Weekend.
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-yellow-400 text-lg md:text-xl mt-2 mb-8 block font-semibold max-w-4xl mx-auto"
            >
              Connect your own AI accounts (OpenAI, Claude, Gemini) • Upload your logo • Use your own domain • Resell this tech to your clients • Keep 100% of the profits.
            </motion.p>

            {/* Testimonial */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white/8 backdrop-blur-lg rounded-xl p-6 border border-white/12 max-w-2xl mx-auto mb-8"
            >
              <h3 className="text-xl font-bold text-yellow-300 mb-3">✨ Built by Marketers, For Marketers</h3>
              <blockquote className="text-lg text-gray-300 italic mb-3">
                "We created Market Genie to solve real marketing challenges. It's designed to help businesses automate their marketing workflows efficiently and effectively, without the complexity and high costs of traditional platforms."
              </blockquote>
              <p className="text-sm text-yellow-400">- Your Market Genie Team</p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.62 }}
              className="mt-4 max-w-4xl mx-auto text-center"
            >
              <p className="text-white font-semibold text-lg md:text-xl">📅 Free Consultation</p>
              <p className="mt-2 text-white text-base md:text-lg">
                <a
                  href="https://calendar.app.google/jPFfeHPMgzMwoCTu8"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-400 underline hover:text-blue-300"
                >
                  Book Your Free Consultation Call
                </a>
              </p>
              <p className="mt-1 text-gray-300 text-sm md:text-base">15-Min Strategy Session</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12 flex items-center justify-center flex-wrap gap-6 text-gray-200"
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Bring your own AI (OpenAI, Claude, Gemini, Perplexity + more)</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Built for agencies and operators</span>
              </div>
            </motion.div>

            {/* Hero Image - Moved above video section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="mt-8 mb-8 flex justify-center"
            >
              <img 
                src="/marketG.png" 
                alt="Market Genie Platform" 
                className="max-w-32 w-full h-auto rounded-lg shadow-2xl border border-gray-700/50"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.75 }}
              className="flex justify-center items-center gap-6 mb-8"
            >
              <a href="https://fazier.com/launches/marketgenie.tech" target="_blank" rel="noreferrer">
                <img src="https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=launched&theme=neutral" width={120} alt="Fazier badge" />
              </a>
              <a href="https://www.producthunt.com/products/market-genie-2-0-ai-marketing-system?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-market-genie-2-0-ai-marketing-system" target="_blank" rel="noopener noreferrer">
                <img alt="Market Genie 2.0 AI Marketing System - We built something you've been wishing for.. Wish granted | Product Hunt" width={250} height={54} src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1144509&theme=light&t=1778520818164" />
              </a>
            </motion.div>

            {/* Video Player Placeholder - Moved to bottom of hero */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex justify-center"
            >
              <div className="bg-black/25 backdrop-blur-lg rounded-xl p-8 border border-white/20 max-w-6xl w-full">
                <div className="text-center mb-6">
                  <h3 className="text-white font-semibold text-2xl mb-2">See the Full Platform in Action</h3>
                    <p className="text-gray-300 text-lg">From lead generation to closed deals, see how Market Genie connects every step of your client acquisition process.</p>
                </div>
                <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg overflow-hidden border border-gray-600">
                  <video 
                    className="w-full h-full object-cover rounded-lg" 
                    controls 
                    autoPlay
                    loop
                    muted
                    preload="auto"
                    poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwMCIgaGVpZ2h0PSI2NzUiIHZpZXdCb3g9IjAgMCAxMjAwIDY3NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjEyMDAiIGhlaWdodD0iNjc1IiBmaWxsPSJ1cmwoI3BhaW50MF9saW5lYXJfNzdfMjcpIi8+CjxjaXJjbGUgY3g9IjYwMCIgY3k9IjMzNy41IiByPSI4MCIgZmlsbD0iIzEwQjk4MSIvPgo8cGF0aCBkPSJNNTcwIDMwN1Y zNjhMNjMwIDMzNy41TDU3MCAzMDdaIiBmaWxsPSJ3aGl0ZSIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyXzc3XzI3IiB4MT0iNjAwIiB5MT0iMCIgeDI9IjYwMCIgeTI9IjY3NSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPgo8c3RvcCBzdG9wLWNvbG9yPSIjMTExODI3Ii8+CjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iIzFGMkEzNyIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo="
                  >
                    <source src="/Final quick for landing page.mp4" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Affiliate CTA Section */}
      <section className="py-20 bg-black/5 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 rounded-2xl p-12 border border-white/10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600 mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
              Would You Like to Become an Affiliate?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              We are giving a 50% affiliate return for a lifetime of your referrals.
            </p>
            <div className="flex justify-center">
              <a
                href="https://marketgenie.promotekit.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black px-8 py-4 rounded-lg text-lg font-bold hover:from-yellow-400 hover:to-orange-400 transition-all duration-200 transform hover:scale-105 inline-flex items-center"
              >
                Become an Affiliate
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-black/8 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600 mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
              Four Core Features That Deliver Results
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              Professional marketing infrastructure designed to help agencies grow efficiently and securely.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white/15 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105"
              >
                <feature.icon className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-gray-300">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-black/5 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600 mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
              Choose the Right Growth Tier
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Three clear paths for operators, growing teams, and agencies ready to launch their own branded SaaS offer.
            </p>
          </div>

          <div className="mb-10 flex flex-col items-center gap-3">
            <div className="inline-flex rounded-full border border-white/15 bg-slate-950/50 p-1 shadow-xl backdrop-blur">
              {['monthly', 'yearly'].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  className={`rounded-full px-5 py-2 text-sm font-semibold transition-all ${
                    billingCycle === cycle
                      ? 'bg-white text-slate-900 shadow'
                      : 'text-gray-200 hover:text-white'
                  }`}
                >
                  {cycle === 'monthly' ? 'Monthly' : 'Yearly'}
                </button>
              ))}
            </div>
            <p className="text-sm text-gray-400">Agency / White Label is always billed yearly.</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {plans.map((plan, index) => {
              const isAgency = plan.id === 'agency_yearly'
              const isActiveCycle = isAgency || plan.cycle === billingCycle

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                  className={`relative h-full rounded-3xl p-8 ${plan.id === 'pro_yearly' ? 'pt-16' : ''} backdrop-blur-xl shadow-2xl transition-all duration-300 ${
                    isAgency
                      ? 'border-2 border-[#38BEBA] bg-slate-950/80 ring-2 ring-[#38BEBA]/25'
                      : isActiveCycle
                        ? 'border border-white/30 bg-slate-950/72'
                        : 'border border-white/10 bg-slate-950/55'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-5 left-0 right-0">
                      <span className={`block w-full rounded-full py-2 text-xs font-bold tracking-wide whitespace-pre-line break-words text-center ${
                        isAgency
                          ? 'bg-[#38BEBA] text-slate-950'
                          : 'bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950'
                      }`}>
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="mb-6">
                    {plan.note && <p className="text-sm text-teal-200 mb-3">{plan.note}</p>}
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-2xl font-bold text-white">{plan.name}</h3>
                    </div>
                    <p className="text-sm text-gray-300 mb-5">{plan.description}</p>
                    {isAgency && (
                      <p className="text-sm text-teal-200 mb-5">Includes full partner dashboard, revenue tracking, and resale tools.</p>
                    )}
                    <div className="flex items-end gap-2 mb-1">
                      <span className="text-5xl font-bold text-white">{plan.priceText}</span>
                      <span className="pb-1 text-gray-300">{plan.subtitle}</span>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-gray-200">
                        <CheckCircle className={`mt-0.5 mr-3 h-5 w-5 flex-shrink-0 ${isAgency ? 'text-teal-300' : 'text-green-400'}`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanPurchase(plan.id)}
                    className={`mt-auto w-full rounded-xl px-6 py-3 font-semibold transition-all duration-200 transform hover:scale-[1.02] ${
                      isAgency
                        ? 'bg-[#38BEBA] text-slate-950 hover:bg-[#2fa7a3]'
                        : plan.id === 'pro_yearly'
                          ? 'bg-white text-slate-900 hover:bg-slate-100'
                          : 'border border-white/20 bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {plan.buttonText}
                  </button>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-black/8 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 via-yellow-300 to-yellow-600 mb-4" style={{textShadow: '2px 2px 4px rgba(0,0,0,0.4), -1px -1px 2px rgba(255,255,255,0.3)', filter: 'brightness(1.1) saturate(1.2)'}}>
              Real Results from Marketers & Agencies Using Market Genie 2.0
            </h2>
            <p className="text-xl text-gray-300">
              Hear from real users who have experienced the difference
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white/8 backdrop-blur-lg rounded-xl p-2 border border-white/12 hover:border-cyan-500/30 transition-all duration-300 flex items-center justify-center"
              >
                <div className="max-w-full max-h-full overflow-hidden rounded-lg">
                  <img
                    src={testimonial.image}
                    alt={testimonial.alt}
                    className="w-auto h-auto max-w-full max-h-full object-contain hover:scale-105 transition-transform duration-300"
                    style={{ transform: 'scale(0.875)', transformOrigin: 'center' }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-16 bg-black/8 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-10 h-10 border border-white/40 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold text-white">Market Genie</span>
              </div>
              <p className="text-gray-300 mb-6 max-w-sm">
                Professional marketing automation that helps you create effective emails, build conversion-focused funnels, and manage campaigns efficiently.
                Grow your business with tools designed for real marketing success.
              </p>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Product</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><Link to="/dashboard" className="hover:text-white transition-colors">Dashboard</Link></li>
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="https://supportgenie.help/customer?tenant=supportgenie-tenant" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Help Center</a></li>
                <li><Link to="/documentation" className="hover:text-white transition-colors">Documentation</Link></li>
                <li><a href="mailto:Help@dubdproducts.com" className="hover:text-white transition-colors">Contact Support</a></li>
                <li><a href="/press-kit.html" className="hover:text-white transition-colors">Press Release</a></li>
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-300">
                <li><a href="/privacy-policy.html" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-of-service.html" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div className="border-t border-white/10 mt-12 pt-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center md:text-left">
                <h4 className="text-white font-semibold mb-2">🤖 AI Support Portal</h4>
                <a 
                  href="https://supportgenie.help/customer?tenant=supportgenie-tenant" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 transition-colors block"
                >
                  Support Genie Help Center
                </a>
                <p className="text-gray-300 text-sm">Available 24/7</p>
              </div>
              <div className="text-center">
                <h4 className="text-white font-semibold mb-2">📅 Free Consultation</h4>
                <a 
                  href="https://calendar.app.google/jPFfeHPMgzMwoCTu8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-yellow-400 hover:text-yellow-300 transition-colors block"
                >
                  Book Your Free Consultation Call
                </a>
                <p className="text-gray-300 text-sm">15-Min Strategy Session</p>
              </div>
              <div className="text-center md:text-right">
                <h4 className="text-white font-semibold mb-2">📧 Email Support</h4>
                <a href="mailto:Help@dubdproducts.com" className="text-yellow-400 hover:text-yellow-300 transition-colors">Help@dubdproducts.com</a>
                <p className="text-gray-300 text-sm">24/7 Response</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 mt-8 pt-8">
            {/* Our Other Applications & Backlinks */}
            <div className="mb-8">
              <h4 className="text-white font-semibold mb-3 text-center md:text-left">🚀 Our Other Applications & Backlinks</h4>
              <div className="flex flex-wrap justify-center md:justify-start gap-6">
                <a href="https://supportgenie.help/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                  Support Genie
                </a>
                <a href="https://dcpro3.com/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                  DC Pro 3
                </a>
                <a href="https://raveup.shop/" target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:text-yellow-300 transition-colors">
                  Rave Up Shop
                </a>
              </div>
            </div>
            
            <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-400 mb-4 md:mb-0">
                © 2026 Market Genie. All rights reserved.
              </div>
              <div className="flex space-x-6 text-gray-400">
                <a href="/privacy-policy.html" className="hover:text-white transition-colors">Privacy Policy</a>
                <a href="/terms-of-service.html" className="hover:text-white transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
